<template>
  <div class="chat-dm-footer" ref="refDm">
    <button v-if="!isMobileSize" type="button" class="button-mic" @click.prevent="actions.toggleDm">
      <IconChevronLeft />
    </button>
    <input v-model="text" type="text" ref="refInputText" maxlength="500" @keyup.enter.prevent="send" />
    <button type="button" :disabled="loading" class="button-primary button-rounded button-big" @click.stop="send">
      <IconLoading v-if="loading" fill-color="white" />
      <IconSend v-else />
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, useTemplateRef, inject, computed } from 'vue';

import IconChevronLeft from '@/components/icons/IconChevronLeft.vue';
import IconLoading from '@/components/icons/IconLoading.vue';
import IconSend from '@/components/icons/IconSend.vue';
import useSize from '@/composables/shared/useSize';
import { getLanguageByCode } from '@/static/languages';
import { useMessageStore } from '@/stores/message';
import { useMicrophoneStore } from '@/stores/microphone';
import { useSocketStore } from '@/stores/socket';
import { useUserStore } from '@/stores/user';
import { InboundMessagePayload } from '@/types/socket/message';
import { DEFAULT_CHAT_UI_STATE } from '@/utils/constants/chat';
import { INITIAL_MESSAGE_TEMPLATE } from '@/utils/constants/socket';
import getSocketId from '@/utils/getSocketId';
import { ij_chat_ui } from '@/utils/injectionKeys';

const socketStore = useSocketStore();
const messageStore = useMessageStore();
const userStore = useUserStore();
const microphoneStore = useMicrophoneStore();

const { isMobileSize } = useSize('refDm');
const refInputText = useTemplateRef('refInputText');

const emit = defineEmits(['scrollBottom']);

const { actions } = inject(ij_chat_ui, DEFAULT_CHAT_UI_STATE);

const text = ref<string>(''); // 입력
const loading = ref<boolean>(false); // 로딩

const currentLanguage = computed(() => {
  return getLanguageByCode(userStore.pageLanguage);
});

const senderId = computed(() => {
  return localStorage.getItem('userIdConnected') ?? '';
});

const formatMessage = (message: string): InboundMessagePayload => {
  return {
    ...INITIAL_MESSAGE_TEMPLATE,
    dataFlag: 'src',
    chatSubId: 1,
    chatLogId: getSocketId(),
    languageCode: currentLanguage.value.code,
    incompleteText: message.replace(/\n/g, ''),
    senderId: senderId.value,
    sentAt: Date.now(),
    status: 'INCOMPLETED',
    dmState: 'PENDING',
    dmTimer: () => {},
  };
};

// 메시지 보내기
const send = async () => {
  if (text.value.trim() === '' || loading.value) {
    return;
  }
  loading.value = true;

  const message = formatMessage(text.value);
  // 임시메시지
  messageStore.upsertMessage(message, 'DIRECT_MESSAGE');
  // 소켓메시지
  socketStore.sendMessageText({
    message: message.incompleteText,
    socketId: message.chatLogId,
  });

  text.value = '';
  focusInput();
  loading.value = false;
};

const focusInput = () => {
  refInputText.value?.focus();
};

const forceReset = () => {
  window.scrollTo(0, 0);
  document.body.scrollTop = 0;
};

onMounted(() => {
  focusInput();
  if (microphoneStore.isMicOn) {
    microphoneStore.stopMic();
  }
  if (refInputText.value) {
    refInputText.value.addEventListener('focus', () => {
      setTimeout(forceReset, 10);
      setTimeout(forceReset, 100);
      setTimeout(forceReset, 250);
    });
  }
  window.addEventListener('scroll', forceReset, { passive: false });
  if (window.visualViewport) {
    window.visualViewport.addEventListener('scroll', forceReset);
    window.visualViewport.addEventListener('resize', forceReset);
  }
});

onUnmounted(() => {
  emit('scrollBottom');
  text.value = '';
  if (refInputText.value) {
    refInputText.value.removeEventListener('focus', forceReset);
  }
  window.removeEventListener('scroll', forceReset);
  if (window.visualViewport) {
    window.visualViewport.removeEventListener('scroll', forceReset);
    window.visualViewport.removeEventListener('resize', forceReset);
  }
});
</script>

<style scoped lang="scss">
.chat-dm-footer {
  position: sticky;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 75rem;
  gap: 0.5rem;
  margin: 0 auto;
  padding: 0.25rem 1rem 1rem 1rem;
  z-index: $z-footer;
}

button {
  padding: 0.75rem !important;
}
</style>
